import {
  OutboundDespatchNoteProduct,
  OutboundPlannedPickLine,
  OutboundProcess,
  PackagingPick,
} from '@features/supplyChain/types';
import dayjs, { Dayjs } from 'dayjs';
import * as Yup from 'yup';
import { nowLocalDate, toLocalDate } from '@utils';
import { ConfirmPickingNoteFormSku } from './types';

type PickedUnits = Pick<
  ConfirmPickingNoteFormSku,
  'wholeUnitsPicked' | 'unitsBroken' | 'looseTaken' | 'amountOfProductInSku'
>;

type PickAgainstPlan = Pick<
  ConfirmPickingNoteFormSku,
  | 'isPlannedLine'
  | 'skuQuantity'
  | 'pickedQuantity'
  | 'plannedProductsToShip'
  | 'productsPicked'
>;

export const productsPerSkuOf = ({
  amountOfProductInSku,
}: Pick<ConfirmPickingNoteFormSku, 'amountOfProductInSku'>) =>
  amountOfProductInSku && amountOfProductInSku > 0 ? amountOfProductInSku : 1;

export const needsDiscrepancyReason = ({
  skuQuantity,
  pickedQuantity,
}: Pick<ConfirmPickingNoteFormSku, 'skuQuantity' | 'pickedQuantity'>) =>
  pickedQuantity < skuQuantity;

export const pickDiffersFromThePlan = ({
  isPlannedLine,
  skuQuantity,
  pickedQuantity,
  plannedProductsToShip,
  productsPicked,
}: PickAgainstPlan) =>
  pickedQuantity !== skuQuantity ||
  (Boolean(isPlannedLine) && productsPicked !== plannedProductsToShip);

const productsObtainableFrom = (units: PickedUnits) => {
  const productsPerSku = productsPerSkuOf(units);
  const wholeUnitsPicked = units.wholeUnitsPicked ?? 0;
  const unitsBroken = units.unitsBroken ?? 0;
  const looseTaken = units.looseTaken ?? 0;
  return {
    fewest: wholeUnitsPicked * productsPerSku + looseTaken,
    most: (wholeUnitsPicked + unitsBroken) * productsPerSku + looseTaken,
  };
};

const listedInPlainEnglish = (labels: string[]) =>
  labels.length > 1
    ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
    : labels.join('');

const productsPickedOutOfRangeMessage = (units: PickedUnits) => {
  const { fewest, most } = productsObtainableFrom(units);
  const allowedRange =
    fewest === most
      ? `Products picked must be ${most}`
      : `Products picked must be between ${fewest} and ${most}`;
  const fieldsToSetToZero = [
    { label: 'Whole units picked', value: units.wholeUnitsPicked },
    { label: 'Units broken', value: units.unitsBroken },
    { label: 'Loose taken', value: units.looseTaken },
  ]
    .filter(({ value }) => (value ?? 0) > 0)
    .map(({ label }) => label);
  const noneFoundHint =
    fieldsToSetToZero.length > 0
      ? ` To record none found, set ${listedInPlainEnglish(
          fieldsToSetToZero
        )} to 0.`
      : '';
  return `${allowedRange} for the units picked.${noneFoundHint}`;
};

const isObtainableFromTheUnitsPicked = (
  productsPicked: number | null | undefined,
  units: PickedUnits
) => {
  if (productsPicked === null || productsPicked === undefined) {
    return true;
  }
  const { fewest, most } = productsObtainableFrom(units);
  return productsPicked >= fewest && productsPicked <= most;
};

export const validationSchema = Yup.object({
  supplyChainProcessId: Yup.string().required(),
  despatchNoteId: Yup.string().required(),
  comment: Yup.string().max(500, 'Max 500 characters.').nullable(),
  pickingDateConfirmation: Yup.string()
    .required('A picking date confirmation is required.')
    .test(
      'a-valid-date',
      'A picking date confirmation is required.',
      (_value, context) => dayjs(context.originalValue).isValid()
    ),
  skus: Yup.array().of(
    Yup.object({
      skuId: Yup.string().required('A SKU ID is required.'),
      packagingComment: Yup.string().nullable(),
      pickedQuantity: Yup.number()
        .typeError('Picked quantity must be a number.')
        .min(0, "Picked quantity can't be negative.")
        .when('isPlannedLine', {
          is: true,
          then: (schema) =>
            schema.max(
              Yup.ref('skuQuantity'),
              ({ max }) =>
                `Whole units picked plus units broken can't be more than the ${max} planned.`
            ),
          otherwise: (schema) =>
            schema.max(
              Yup.ref('skuQuantity'),
              "Picked quantity can't be greater than ordered quantity."
            ),
        }),
      wholeUnitsPicked: Yup.number()
        .typeError('Whole units must be a number.')
        .min(0, "Whole units can't be negative.")
        .nullable(),
      unitsBroken: Yup.number()
        .typeError('Units broken must be a number.')
        .min(0, "Units broken can't be negative.")
        .nullable(),
      looseTaken: Yup.number()
        .typeError('Loose taken must be a number.')
        .min(0, "Loose taken can't be negative.")
        .nullable()
        .when('isPlannedLine', {
          is: true,
          then: (schema) =>
            schema.max(
              Yup.ref('plannedLooseToTake'),
              ({ max }) => `Loose taken can't be more than the ${max} planned.`
            ),
        }),
      productsPicked: Yup.number()
        .typeError('Products picked must be a number.')
        .min(0, "Products picked can't be negative.")
        .nullable()
        .when('isPlannedLine', {
          is: true,
          then: (schema) =>
            schema.test(
              'obtainable-from-the-units-picked',
              (productsPicked, context) =>
                isObtainableFromTheUnitsPicked(
                  productsPicked,
                  context.parent
                ) ||
                context.createError({
                  message: productsPickedOutOfRangeMessage(context.parent),
                })
            ),
        }),
      discrepancyReason: Yup.string()
        .nullable()
        .when(['skuQuantity', 'pickedQuantity'], {
          is: (skuQuantity: number, pickedQuantity: number) =>
            needsDiscrepancyReason({ skuQuantity, pickedQuantity }),
          then: () =>
            Yup.string().trim().required('A discrepancy reason is required.'),
        }),
    })
  ),
});

const productNameForPlannedLine = (
  plannedLine: OutboundPlannedPickLine,
  products: OutboundDespatchNoteProduct[]
) =>
  products.find((product) => product.productId === plannedLine.productId)
    ?.productName ?? '';

const skuFromPlannedLine = (
  plannedLine: OutboundPlannedPickLine,
  products: OutboundDespatchNoteProduct[]
): ConfirmPickingNoteFormSku => {
  const pickedQuantity =
    plannedLine.wholeUnitsToPick + plannedLine.unitsToBreak;
  return {
    productId: plannedLine.productId ?? '',
    productName: productNameForPlannedLine(plannedLine, products),
    skuId: plannedLine.skuId,
    skuIdentifier: plannedLine.skuIdentifier ?? '',
    skuName:
      plannedLine.skuName ?? productNameForPlannedLine(plannedLine, products),
    skuQuantity: pickedQuantity,
    pickedQuantity,
    wholeUnitsPicked: plannedLine.wholeUnitsToPick,
    unitsBroken: plannedLine.unitsToBreak,
    looseTaken: plannedLine.looseToTake,
    productsPicked: plannedLine.productsToShip,
    plannedProductsToShip: plannedLine.productsToShip,
    plannedLooseToTake: plannedLine.looseToTake,
    amountOfProductInSku: plannedLine.amountOfProductInSku ?? 1,
    packaging: plannedLine.packaging ?? null,
    isPlannedLine: true,
    packagingComment: null,
    discrepancyReason: null,
    packagingDetails: null,
  };
};

const pickingDateNoLaterThanToday = (requiredPickingDate: Dayjs) =>
  toLocalDate(requiredPickingDate) > nowLocalDate()
    ? dayjs(nowLocalDate())
    : requiredPickingDate;

export const makeDefaultValues = (selectedOutboundProcess: OutboundProcess) => {
  if (!selectedOutboundProcess || !selectedOutboundProcess.despatchNote) {
    return {
      supplyChainProcessId: '',
      despatchNoteId: '',
      comment: '',
      skus: [],
      pickingDateConfirmation: '',
    };
  }
  const { despatchNote, pickingNote } = selectedOutboundProcess;
  const products = despatchNote.products ?? [];
  const skusFromDespatch = despatchNote.skus.map(
    (sku): ConfirmPickingNoteFormSku => ({
      productId: sku.productId,
      productName: sku.productName,
      skuId: sku.skuId,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      skuIdentifier: sku.skuIdentifier!,
      skuName: sku.name,
      skuQuantity: sku.skuQuantity,
      pickedQuantity: sku.skuQuantity,
      amountOfProductInSku: sku.amountOfProductInSku,
      unitOfMeasure: sku.unitOfMeasure,
      packagingComment: null,
      discrepancyReason: null,
      packagingDetails: sku.packagingDetails,
    })
  );
  const skusFromProductPlans = (pickingNote?.plannedLines ?? [])
    .filter((plannedLine) => Boolean(plannedLine.productId))
    .map((plannedLine) => skuFromPlannedLine(plannedLine, products));
  return {
    supplyChainProcessId: selectedOutboundProcess.id,
    despatchNoteId: despatchNote.id,
    comment: '',
    skus: [...skusFromDespatch, ...skusFromProductPlans],
    pickingDateConfirmation: pickingDateNoLaterThanToday(
      dayjs(despatchNote.requiredPickingDate.dateTime).tz(
        despatchNote.requiredPickingDate.timeZone
      )
    ),
  };
};

const sumOf = (values: (number | undefined)[]) =>
  values.reduce<number>((total, value) => total + (value ?? 0), 0);

const joinWrittenNotes = (notes: (string | null | undefined)[]) => {
  const writtenNotes = notes
    .map((note) => note?.trim() ?? '')
    .filter((note) => note.length > 0);
  return writtenNotes.length > 0 ? writtenNotes.join('; ') : null;
};

const addUpPackagingById = (rows: ConfirmPickingNoteFormSku[]) => {
  const packagingById = new Map<string, PackagingPick>();
  rows
    .flatMap((row) => row.packaging ?? [])
    .forEach((pick) => {
      const counted = packagingById.get(pick.packagingId);
      packagingById.set(
        pick.packagingId,
        counted
          ? { ...counted, quantity: counted.quantity + pick.quantity }
          : pick
      );
    });
  return packagingById.size > 0 ? Array.from(packagingById.values()) : null;
};

const combineRowsOfOneSku = (
  rows: ConfirmPickingNoteFormSku[]
): ConfirmPickingNoteFormSku => {
  const [firstRow] = rows;
  if (rows.length === 1) {
    return firstRow;
  }
  const writtenNotes = {
    discrepancyReason: joinWrittenNotes(
      rows.map((row) => row.discrepancyReason)
    ),
    packagingComment: joinWrittenNotes(rows.map((row) => row.packagingComment)),
  };
  const pickedQuantity = sumOf(rows.map((row) => row.pickedQuantity));
  const plannedRows = rows.filter((row) => row.isPlannedLine);
  if (plannedRows.length === 0) {
    return { ...firstRow, ...writtenNotes, pickedQuantity };
  }
  const skuLinePicked = sumOf(
    rows.filter((row) => !row.isPlannedLine).map((row) => row.pickedQuantity)
  );
  return {
    ...firstRow,
    ...writtenNotes,
    pickedQuantity,
    wholeUnitsPicked:
      skuLinePicked + sumOf(plannedRows.map((row) => row.wholeUnitsPicked)),
    unitsBroken: sumOf(plannedRows.map((row) => row.unitsBroken)),
    looseTaken: sumOf(plannedRows.map((row) => row.looseTaken)),
    productsPicked:
      skuLinePicked * productsPerSkuOf(plannedRows[0]) +
      sumOf(plannedRows.map((row) => row.productsPicked)),
    packaging: addUpPackagingById(plannedRows),
  };
};

export const oneConfirmationLinePerSku = (
  rows: ConfirmPickingNoteFormSku[]
): ConfirmPickingNoteFormSku[] => {
  const rowsBySku = new Map<string, ConfirmPickingNoteFormSku[]>();
  rows.forEach((row) => {
    rowsBySku.set(row.skuId, [...(rowsBySku.get(row.skuId) ?? []), row]);
  });
  if (rowsBySku.size === rows.length) {
    return rows;
  }
  return Array.from(rowsBySku.values()).map(combineRowsOfOneSku);
};
